/** Settings · AI Brain (spec 15 §9) — data shapes for General, Train ELVA and the Knowledge base. */

/* ───────────────────────── General behaviour (§9.1) ─────────────────────── */

/** One of twelve ranked purposes. Fixed by ELVA — agents map onto the list. */
export interface BrainPurpose {
  rank: number;
  label: string;
  /** Which agent raises it. */
  raisedBy: string;
  /** Only where the rule needs saying. */
  note?: string;
  /** Row 9 expands to the practice's own recall campaigns. */
  campaigns?: { rank: number; name: string; interval: string }[];
}

/** A platform-wide limit. Rendered locked: neither a practice nor ELVA can switch it off. */
export interface HardLimit {
  id: string;
  label: string;
  desc: string;
}

/** A practice-owned toggle — its risk, which is why it is editable (§9.1). */
export interface BrainToggle {
  id: string;
  label: string;
  desc: string;
  enabled: boolean;
}

export type PriceQuoting = 'starting_at' | 'range' | 'refer_to_staff';

/** One language ELVA speaks with patients (§2 card 06). */
export interface BrainLanguage {
  id: string;
  label: string;
  channels: string; // "Texts, emails and AI calls"
  patients: number;
  locked?: boolean;
}

export interface BrainData {
  purposes: BrainPurpose[];
  tieBreakers: string[];
  name: string;
  /** The selected voice name (e.g. `Gleam`). */
  voice: string;
  /** GPT-Live-1's built-in voices, grouped by accent (spec 44 §2). */
  voiceGroups: { group: string; voices: { name: string; description: string }[] }[];
  warmth: number;
  formality: number;
  respondInPatientLanguage: boolean;
  priceQuoting: PriceQuoting;
  brandColour: string;
  hardLimits: HardLimit[];
  hipaa: BrainToggle[];
  money: BrainToggle[];
  recording: BrainToggle[];
  /** Rendered as data, and the count in the copy derives from it (§3.1). */
  twoPartyConsentStates: string[];
  /* ── amendment 32 (simplified Brain) ───────────────────────────────── */
  /** This location's state — decides the two-party consent line (§2 card 07). */
  locationState: string;
  /** How often ELVA starts a conversation, per patient (§2 card 02). 1–5. */
  contactLimit: number;
  /** Combine two same-day messages into one. */
  combineMessages: boolean;
  /** The chips of what is never counted toward the limit. */
  neverCounted: string[];
  /** Say a balance amount out loud, after verifying identity (§2 card 04). */
  sayBalance: boolean;
  /** The languages ELVA speaks (§2 card 06). */
  languages: BrainLanguage[];
  /** What "Add a language" offers. */
  languagesAvailable: string[];
  /** The stop-card footer counts (§2 card 10). */
  stopStats: { textsOff: number; stoppedTopic: number };
}

/* ── Your rules (amendment 32 §3, board BR2Train card 01) ─────────────────── */

export type BrainRuleStatus = 'active' | 'needs_change';

export interface BrainRule {
  id: string;
  /** The rule as the practice wrote it. */
  text: string;
  /** ELVA's one-sentence reading. */
  readsAs: string;
  /** "Every agent" or a specific agent label. */
  appliesTo: string;
  addedBy: string;
  addedAt: string;
  usedCount?: number;
  status: BrainRuleStatus;
  /** When it needs a change: what to do and the deep-link (Move to Knowledge base). */
  fix?: { note: string; sentence: string; category: KbCategory };
}

/* ─────────────────────────── Train ELVA (§9.2) ──────────────────────────── */

export type TrainKind = 'answer_to_check' | 'unanswered_question' | 'close_call_booking';

export interface TrainRow {
  id: string;
  kind: TrainKind;
  /** How long it has waited — the cards show the longest-waiting first. */
  waitedDays: number;
  // answer_to_check
  channel?: 'call' | 'sms' | 'email';
  patientName?: string;
  patientSaid?: string;
  answeredFromEntry?: string;
  /** The second trigger is the point: a clinical answer deserves review even when confident. */
  raisedBecause?: 'weak_match' | 'clinical';
  raisedDetail?: string;
  // unanswered_question
  question?: string;
  askedCount?: number;
  elvaDid?: string;
  lastAsked?: string;
  // close_call_booking
  bookedReason?: string;
  runnerUpReason?: string;
  queuedBecause?: 'close_call' | 'nothing_near';
}

/* ────────────────────────── Knowledge base (§9.3) ───────────────────────── */

export type KbCategory =
  | 'practice_info'
  | 'services'
  | 'fees'
  | 'insurance'
  | 'policies'
  | 'providers'
  | 'forms'
  | 'faq';

/** Eight fixed categories. An open list means two offices file the same thing in two places. */
export const KB_CATEGORIES: { id: KbCategory; label: string }[] = [
  { id: 'practice_info', label: 'Practice info' },
  { id: 'services', label: 'Services and procedures' },
  { id: 'fees', label: 'Fees and pricing' },
  { id: 'insurance', label: 'Insurance and payment' },
  { id: 'policies', label: 'Policies' },
  { id: 'providers', label: 'Providers and staff' },
  { id: 'forms', label: 'Forms' },
  { id: 'faq', label: 'Frequently asked' },
];

export const KB_CATEGORY_LABEL: Record<KbCategory, string> = KB_CATEGORIES.reduce(
  (a, c) => ({ ...a, [c.id]: c.label }),
  {} as Record<KbCategory, string>,
);

export interface KnowledgeEntry {
  id: string;
  category: KbCategory;
  /** Must be a question — it is the matching surface (§9.3). */
  title: string;
  body: string;
  enabled: boolean;
  updatedAt: string;
  /** How many times ELVA answered from this entry, last 30 days. */
  usedCount?: number;
  /** Optional phrasings patients also use (§4). */
  alsoAsk?: string;
  /** A sentence an agent may never say — highlighted, with an amber note (§4). */
  lockedSentence?: string;
  lockedNote?: string;
}

/** Facts pulled read-only from Practice management (§4). */
export interface KbPracticeFacts {
  officeHours: string;
  address: string;
  phone: string;
  doctors: string;
}

export interface KnowledgeBaseData {
  entries: KnowledgeEntry[];
  /** Answers drawn from an entry in the last 30 days. */
  answeredFromEntry: number;
  /** Questions no entry covered — the count and a link, the rows live on Train ELVA. */
  couldNotAnswer: number;
  /** Read-only facts shown at the top, owned by Practice management (§4). */
  practiceFacts: KbPracticeFacts;
}
